import { useState } from "react";
import PortalNavbar from "@/components/feature/PortalNavbar";
import PortalFooter from "@/components/feature/PortalFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import { brandValues } from "@/mocks/portal";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dagkvc1tto0ceqqnhudg";
type Status = { state: "idle" | "loading" | "success" | "error"; message: string };

const principles = [
  "坚持转载与整理并重的信息导航，不渲染、不造谣、不传播未经核实的内容。",
  "以客观呈现热点动态与多方声音为原则，帮助用户自行判断，而非代替用户下结论。",
  "清晰标注信息栏目与时间节点，方便回溯查证，保证内容可追溯、可复核。",
  "尊重当事人基本权利，涉及个人话题仅限公开信息，避免无依据的恶意揣测与人身攻击。",
];

const testimonials = [
  {
    name: "小鹿",
    role: "综艺爱好者",
    text: "每天打开曝光导航已经成了习惯，热门排行和栏目分类都很清晰，追综艺再也不怕漏梗了。",
    avatar: "A",
    color: "bg-primary-500",
  },
  {
    name: "阿橙",
    role: "吃瓜老手",
    text: "最喜欢往期回顾的月度复盘，即使中途没跟上，也能用几分钟把上个月的热点补回来，很实用。",
    avatar: "橙",
    color: "bg-accent-500",
  },
  {
    name: "阿远",
    role: "影视从业者",
    text: "作为行业相关从业者，这里的内容整理得比较客观，很多幕后与趋势解读对我很有参考价值。",
    avatar: "远",
    color: "bg-secondary-600",
  },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于曝光导航",
  description: "曝光导航是一个专注娱乐热点的信息导航平台，聚合综艺影视音乐时尚动态，坚持客观、及时、可回溯的内容原则。",
  inLanguage: "zh-CN",
};

export default function AboutPage() {
  const [status, setStatus] = useState<Status>({ state: "idle", message: "" });

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus({ state: "success", message: "感谢反馈，我们会尽快处理！" });
      form.reset();
      return;
    }
    fd.delete("company_alt");
    setStatus({ state: "loading", message: "" });
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let code = "";
      try {
        const parsed = JSON.parse(text);
        code = parsed?.code || "";
      } catch {
        code = "";
      }
      if (res.ok && code === "OK") {
        setStatus({ state: "success", message: "感谢反馈，我们会认真阅读每一封来信！" });
        form.reset();
      } else {
        setStatus({ state: "error", message: "提交失败，请稍后再试。" });
      }
    } catch {
      setStatus({ state: "error", message: "网络异常，请稍后再试。" });
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Seo
        title="关于我们 - 曝光导航"
        description="了解曝光导航的品牌定位、发展理念与内容原则。我们是一站式娱乐热点信息导航平台，坚持及时、客观、可回溯，欢迎用户参与监督与反馈。"
        keywords="关于我们,曝光导航,内容原则,品牌介绍,意见反馈"
        schema={schema}
      />
      <PortalNavbar />
      <main className="flex-1">
        {/* 页头 */}
        <section className="bg-background-100/60">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
            <nav aria-label="面包屑" className="mb-6 flex items-center gap-1.5 text-xs text-foreground-500">
              <a href="/" className="hover:text-primary-600">首页</a>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                <i className="ri-flashlight-line text-2xl" />
              </span>
              <div>
                <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">关于曝光导航</h1>
                <p className="mt-1 text-sm text-foreground-600">一站式娱乐热点信息导航平台</p>
              </div>
            </div>
          </div>
        </section>

        {/* 品牌介绍 */}
        <section className="bg-background-50">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-12 md:px-6 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">我们是谁</h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                曝光导航是一个专注娱乐热点的信息导航平台。面对海量且碎片化的综艺、影视、音乐、时尚动态，
                我们通过「今日更新 + 热门排行 + 内容栏目 + 往期回顾」的信息结构，把分散在社交平台与媒体上的
                热点聚合为清晰、及时、可回溯的导航，让关注娱乐内容的用户可以高效掌握圈内风向。
              </p>
              <p className="mt-4 text-sm leading-7 text-foreground-600">
                我们不制造话题，只做热点的「整理者」与「导航员」。每条内容都标注栏目与时间，帮助你快速定位、
                自主判断，把主动权交还给关注热点的你。
              </p>

              <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950">发展理念</h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                从简单的热点链接集，到如今具备排行体系、栏目导航与月度复盘的信息门户，曝光导航始终围绕三个关键词演进：
                及时、聚合、清晰。我们相信，好的信息工具应当既快又稳，既全又省心，在信息过载的时代帮你保留专注力。
              </p>
            </div>

            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">我们的价值</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {brandValues.map((v) => (
                  <div key={v.title} className="rounded-xl border border-background-200 bg-background-100 p-5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-50 text-primary-600">
                      <i className={`${v.icon} text-xl`} />
                    </span>
                    <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                    <p className="mt-1 text-sm text-foreground-600">{v.desc}</p>
                  </div>
                ))}
              </div>

              <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950">内容原则</h2>
              <ul className="mt-4 space-y-3">
                {principles.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm leading-6 text-foreground-700">
                    <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-600 text-background-50">
                      <i className="ri-check-line text-xs" />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 用户口碑 */}
        <section className="bg-background-100/60">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
            <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-3xl">用户怎么说</h2>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-xl border border-background-200 bg-background-50 p-6">
                  <i className="ri-double-quotes-l text-2xl text-accent-500" />
                  <blockquote className="mt-2 text-sm leading-7 text-foreground-700">{t.text}</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-full ${t.color} text-sm font-bold text-background-50`}>
                      {t.avatar}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 联系反馈 */}
        <section id="contact" className="scroll-mt-20 bg-background-50">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-14 md:px-6 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                <i className="ri-mail-send-line" />
                联系我们
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">一起让导航更懂你</h2>
              <p className="mt-3 max-w-lg text-sm leading-7 text-foreground-600">
                无论是栏目建议、内容纠错还是合作洽谈，都欢迎通过右侧表单留言。
                我们会在工作时间内尽快回复每一封来信。
              </p>
              <ul className="mt-6 space-y-4">
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-100 text-primary-600"><i className="ri-mail-line" /></span>
                  <div>
                    <p className="text-xs text-foreground-500">商务与合作</p>
                    <p className="text-sm font-medium text-foreground-900">hello@expose-nav.example</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-100 text-accent-700"><i className="ri-time-line" /></span>
                  <div>
                    <p className="text-xs text-foreground-500">服务时间</p>
                    <p className="text-sm font-medium text-foreground-900">周一至周五 9:00 - 18:00</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-100 p-6 md:p-8">
              <h3 className="font-heading text-xl font-bold text-foreground-950">意见反馈</h3>
              <form data-readdy-form="contact-fb" onSubmit={handleSubmit} className="mt-5 space-y-4">
                <input name="company_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                <div className="flex flex-col gap-4 sm:flex-row">
                  <div className="flex-1">
                    <label htmlFor="fb-name" className="mb-1.5 block text-sm text-foreground-700">称呼</label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      placeholder="你的称呼"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="fb-email" className="mb-1.5 block text-sm text-foreground-700">邮箱</label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于回复你"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="fb-msg" className="mb-1.5 block text-sm text-foreground-700">留言内容</label>
                  <textarea
                    id="fb-msg"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请写下你的建议、纠错或合作意向（500字以内）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {status.state === "loading" ? "提交中..." : (
                    <>
                      <i className="ri-send-plane-fill" />
                      提交反馈
                    </>
                  )}
                </button>
                {status.state === "success" && (
                  <p className="flex items-center gap-1.5 rounded-md bg-secondary-600/15 px-3 py-2 text-sm text-secondary-800">
                    <i className="ri-checkbox-circle-fill" />{status.message}
                  </p>
                )}
                {status.state === "error" && (
                  <p className="flex items-center gap-1.5 rounded-md bg-primary-500/15 px-3 py-2 text-sm text-primary-700">
                    <i className="ri-error-warning-line" />{status.message}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <PortalFooter />
      <BackToTop />
    </div>
  );
}